import { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  VIDEO_DESCRIPTION_MAX,
  VIDEO_TITLE_MAX,
  VideoMetadataInput,
  type VideoMetadataInput as VideoMetadataInputType,
} from "@vedaunion/shared";
import { useYoutubeCategories } from "../../lib/youtube";
import { TagsInput } from "./TagsInput";
import { PlaylistAssignment } from "./PlaylistAssignment";
import { ThumbnailPicker } from "./ThumbnailPicker";

/**
 * Shared metadata form for both Upload and EditVideo pages. Uses React
 * Hook Form + Zod resolver matching `VideoMetadataInput`.
 *
 * Layout:
 *   Section 1: Title & description
 *   Section 2: Privacy & schedule
 *   Section 3: Audience (made-for-kids, AI-generated, category, language)
 *   Section 4: Thumbnail
 *   Section 5: Series (playlists)
 *   Section 6: Advanced (collapsible: embeddable / public stats / license /
 *              recording date / notify subscribers / localizations)
 *
 * The form value type is `VideoMetadataInputType` (full Tier 1+2). For
 * the Edit page, we apply a `defaultValues` from the loaded admin detail.
 */

const REGISTER_LANGUAGES: Array<{ code: string; label: string }> = [
  { code: "", label: "—" },
  { code: "en", label: "English" },
  { code: "hr", label: "Hrvatski (Croatian)" },
  { code: "sl", label: "Slovenščina (Slovenian)" },
  { code: "sa", label: "संस्कृत (Sanskrit)" },
  { code: "hi", label: "हिन्दी (Hindi)" },
  { code: "ta", label: "தமிழ் (Tamil)" },
  { code: "es", label: "Español" },
  { code: "fr", label: "Français" },
  { code: "de", label: "Deutsch" },
];

export interface VideoMetadataFormProps {
  defaultValues?: Partial<VideoMetadataInputType>;
  /** Existing thumbnail URL (Edit page only). */
  existingThumbnailUrl?: string | null;
  /**
   * Hook to receive the cropped thumbnail Blob when the editor changes
   * the thumbnail. The parent decides when to upload it (e.g. after
   * the metadata save succeeds).
   */
  onThumbnailChange?: (blob: Blob | null) => void;
  /** Submit handler. */
  onSubmit: (values: VideoMetadataInputType) => Promise<void> | void;
  submitLabel: string;
  submittingLabel?: string;
  submitting?: boolean;
  /** Renders below the action bar — for delete buttons, captions, etc. */
  trailing?: React.ReactNode;
  /** Error to surface at the top of the form (e.g., quota exceeded). */
  topError?: string | null;
}

export function VideoMetadataForm(props: VideoMetadataFormProps) {
  const categoriesQ = useYoutubeCategories();
  const categories = categoriesQ.data?.categories ?? [];
  const [thumbBlob, setThumbBlob] = useState<Blob | null>(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const {
    control,
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<VideoMetadataInputType>({
    // Cast: the Zod schema's input vs. output types differ slightly in
    // optionality (Zod's `.default()` makes the field required on output
    // but optional on input). RHF wants one type. We use the output type
    // throughout and tell RHF to trust the resolver.
    resolver: zodResolver(VideoMetadataInput) as never,
    defaultValues: {
      title: "",
      description: "",
      tags: [],
      // Empty by default — editor explicitly picks a category from the list.
      // Server's `buildVideoResource` skips the field entirely when empty,
      // so YouTube applies its own default (none / unspecified).
      categoryId: "",
      privacyStatus: "private",
      publishAt: null,
      selfDeclaredMadeForKids: false,
      containsSyntheticMedia: false,
      playlistIds: [],
      embeddable: true,
      publicStatsViewable: true,
      license: "youtube",
      defaultLanguage: null,
      defaultAudioLanguage: null,
      recordingDate: null,
      notifySubscribers: true,
      localizations: [],
      ...props.defaultValues,
    },
  });

  const titleValue = watch("title") ?? "";
  const descriptionValue = watch("description") ?? "";
  const privacyValue = watch("privacyStatus") ?? "private";

  const renderSubmitLabel = () => {
    return props.submitLabel.replace(
      "{{privacy}}",
      privacyValue.charAt(0).toUpperCase() + privacyValue.slice(1)
    );
  };

  return (
    <form
      onSubmit={handleSubmit(props.onSubmit)}
      className="yt-form"
      noValidate
    >
      <fieldset disabled={!!props.submitting} style={{ margin: 0, padding: 0, border: 'none' }}>
      {props.topError && (
        <p className="yt-admin-error" role="alert">
          {props.topError}
        </p>
      )}

      {/* SECTION 1 — Title & description */}
      <section className="yt-form-section">
        <h3 className="yt-form-section-title">Title &amp; description</h3>
        <Field
          label="Title"
          error={errors.title?.message}
          counter={`${titleValue.length}/${VIDEO_TITLE_MAX}`}
          counterOver={titleValue.length > VIDEO_TITLE_MAX}
        >
          <input
            type="text"
            className="auth-input"
            maxLength={VIDEO_TITLE_MAX + 20}
            {...register("title")}
          />
        </Field>
        <Field
          label="Description"
          error={errors.description?.message}
          counter={`${descriptionValue.length}/${VIDEO_DESCRIPTION_MAX}`}
          counterOver={descriptionValue.length > VIDEO_DESCRIPTION_MAX}
        >
          <textarea
            rows={8}
            className="auth-input"
            {...register("description")}
          />
        </Field>
      </section>

      {/* SECTION 2 — Privacy & schedule */}
      <section className="yt-form-section">
        <h3 className="yt-form-section-title">Privacy &amp; schedule</h3>
        <div className="yt-radio-group">
          {(
            [
              {
                value: "private",
                label: "Private",
                hint: "Only people you share the link with.",
              },
              {
                value: "unlisted",
                label: "Unlisted",
                hint: "Anyone with the link can watch.",
              },
              {
                value: "public",
                label: "Public",
                hint: "Visible on the channel + search.",
              },
            ] as const
          ).map((opt) => (
            <label key={opt.value} className="yt-radio-row">
              <input
                type="radio"
                value={opt.value}
                {...register("privacyStatus")}
              />
              <span className="yt-radio-label">
                <strong>{opt.label}</strong>
                <span className="yt-radio-hint">{opt.hint}</span>
              </span>
            </label>
          ))}
        </div>
        {privacyValue === "private" && (
          <Field
            label="Schedule publish (optional)"
            hint="Leave empty to keep private indefinitely. Schedule will publish at this time."
            error={errors.publishAt?.message}
          >
            <input
              type="datetime-local"
              className="auth-input"
              {...register("publishAt", {
                setValueAs: (v) => (v ? new Date(v).toISOString() : null),
              })}
            />
          </Field>
        )}
      </section>

      {/* SECTION 3 — Audience */}
      <section className="yt-form-section">
        <h3 className="yt-form-section-title">Audience</h3>
        <div className="yt-toggle-row">
          <label>
            <input
              type="checkbox"
              {...register("selfDeclaredMadeForKids")}
            />
            <span>
              <strong>Made for kids</strong>
              <span className="yt-radio-hint">
                Required by COPPA. Educational content for adults — leave off.
              </span>
            </span>
          </label>
        </div>
        <div className="yt-toggle-row">
          <label>
            <input
              type="checkbox"
              {...register("containsSyntheticMedia")}
            />
            <span>
              <strong>Contains AI-generated content</strong>
              <span className="yt-radio-hint">
                Required disclosure if any part of the video was synthesised
                by AI.
              </span>
            </span>
          </label>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Category">
            <select className="auth-input" {...register("categoryId")}>
              <option value="">— Pick a category —</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.title}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Tags">
            <Controller
              control={control}
              name="tags"
              render={({ field }) => (
                <TagsInput
                  value={field.value ?? []}
                  onChange={field.onChange}
                />
              )}
            />
          </Field>
        </div>
      </section>

      {/* SECTION 4 — Thumbnail */}
      <section className="yt-form-section">
        <h3 className="yt-form-section-title">Thumbnail</h3>
        <ThumbnailPicker
          uploadedUrl={props.existingThumbnailUrl ?? null}
          draftBlob={thumbBlob}
          onCrop={(blob) => {
            setThumbBlob(blob);
            props.onThumbnailChange?.(blob);
          }}
          onClearDraft={() => {
            setThumbBlob(null);
            props.onThumbnailChange?.(null);
          }}
        />
      </section>

      {/* SECTION 5 — Series assignment */}
      <section className="yt-form-section">
        <h3 className="yt-form-section-title">Series</h3>
        <Controller
          control={control}
          name="playlistIds"
          render={({ field }) => (
            <PlaylistAssignment
              value={field.value ?? []}
              onChange={field.onChange}
            />
          )}
        />
      </section>

      {/* SECTION 6 — Advanced disclosure */}
      <section className="yt-form-section">
        <button
          type="button"
          className="yt-form-section-toggle"
          aria-expanded={advancedOpen}
          onClick={() => setAdvancedOpen((o) => !o)}
        >
          <span className="font-display-italic">
            {advancedOpen ? "− Advanced settings" : "+ Advanced settings"}
          </span>
        </button>
        {advancedOpen && (
          <div className="yt-form-section-body">
            <div className="yt-toggle-row">
              <label>
                <input type="checkbox" {...register("embeddable")} />
                <span>
                  <strong>Allow embedding on other sites</strong>
                  <span className="yt-radio-hint">
                    Required for our own /youtube library to play this video
                    inline. Strongly recommended on.
                  </span>
                </span>
              </label>
            </div>
            <div className="yt-toggle-row">
              <label>
                <input type="checkbox" {...register("publicStatsViewable")} />
                <span>
                  <strong>Show like + view counts on YouTube</strong>
                </span>
              </label>
            </div>
            <Field label="License">
              <select className="auth-input" {...register("license")}>
                <option value="youtube">Standard YouTube License</option>
                <option value="creativeCommon">Creative Commons (BY)</option>
              </select>
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Default language">
                <select
                  className="auth-input"
                  {...register("defaultLanguage", {
                    setValueAs: (v) => (v === "" ? null : v),
                  })}
                >
                  {REGISTER_LANGUAGES.map((l) => (
                    <option key={l.code} value={l.code}>
                      {l.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Audio language">
                <select
                  className="auth-input"
                  {...register("defaultAudioLanguage", {
                    setValueAs: (v) => (v === "" ? null : v),
                  })}
                >
                  {REGISTER_LANGUAGES.map((l) => (
                    <option key={l.code} value={l.code}>
                      {l.label}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            <Field
              label="Recording date (optional)"
              hint="When the video was actually filmed."
            >
              <input
                type="date"
                className="auth-input"
                {...register("recordingDate", {
                  setValueAs: (v) => (v === "" ? null : v),
                })}
              />
            </Field>
            <div className="yt-toggle-row">
              <label>
                <input type="checkbox" {...register("notifySubscribers")} />
                <span>
                  <strong>Notify subscribers on publish</strong>
                  <span className="yt-radio-hint">
                    Sends the standard YouTube notification when the video
                    becomes public. Ignored when keeping it private.
                  </span>
                </span>
              </label>
            </div>
          </div>
        )}
      </section>

      <div className="yt-form-action-bar">
        <button
          type="submit"
          className="btn-app"
          disabled={!!props.submitting}
        >
          {props.submitting ? (props.submittingLabel ?? "Saving…") : renderSubmitLabel()}
        </button>
        {props.trailing}
      </div>
      </fieldset>
    </form>
  );
}

function Field({
  label,
  error,
  hint,
  counter,
  counterOver,
  children,
}: {
  label: string;
  error?: string;
  hint?: string;
  counter?: string;
  counterOver?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="auth-field">
      <div className="auth-label-row">
        <label className="auth-label">{label}</label>
        {counter && (
          <span
            className={`auth-counter tabular ${counterOver ? "is-over" : ""}`}
          >
            {counter}
          </span>
        )}
      </div>
      {children}
      {error && <p className="auth-error">— {error}</p>}
      {!error && hint && <p className="auth-hint">{hint}</p>}
    </div>
  );
}
